import { resolveContactProfilePicUrl } from "../utils/resolveContactProfilePicUrl";
import { getTicketDisplayName } from "../utils/ticketDisplayName";

// Notification service for desktop and mobile notifications
class NotificationService {
  constructor() {
    this.permission = null;
    this.currentTicketId = null;
    this.enabled = true;
    this.init();
  }

  async init() {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return;
    }

    // Check current permission
    this.permission = Notification.permission;

    // Request permission if not already granted or denied
    if (this.permission === 'default') {
      try {
        this.permission = await Notification.requestPermission();
      } catch (err) {
        console.error('Error requesting notification permission:', err);
      }
    }

    // Subscribe to push notifications for background notifications
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (!subscription && this.permission === 'granted') {
          // Subscribe to push notifications
          await this.subscribeToPush(registration);
        }
      } catch (err) {
        console.error('Error setting up push subscription:', err);
      }
    }

    // Listen for visibility changes to ensure notifications work when tab is hidden
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        // When tab becomes visible, we can update notification behavior if needed
        // This ensures notifications work properly when tab is hidden/visible
      });
    }
  }

  async subscribeToPush(registration) {
    try {
      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        // Verify subscription is still valid on backend
        await this.sendSubscriptionToBackend(existingSubscription);
        return existingSubscription;
      }
      
      // Get VAPID public key from backend
      const api = (await import('./api')).default;
      let publicKey;
      try {
        const response = await api.get('/push/public-key');
        publicKey = response.data.publicKey;
      } catch (err) {
        console.error('Error getting VAPID public key:', err);
        return null;
      }

      if (!publicKey) {
        console.warn('VAPID public key not available - push notifications disabled');
        return null;
      }

      // Convert VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(publicKey);
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);
      
      console.log('Push subscription successful');
      return subscription;
    } catch (err) {
      console.error('Error subscribing to push:', err);
      return null;
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async sendSubscriptionToBackend(subscription) {
    try {
      const api = (await import('./api')).default;
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        }
      };
      
      await api.post('/push/subscribe', subscriptionData);
      console.log('Push subscription saved to backend');
    } catch (err) {
      console.error('Error sending subscription to backend:', err);
    }
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  setCurrentTicket(ticketId) {
    this.currentTicketId = ticketId;
  }

  setEnabled(enabled) {
    this.enabled = enabled;
  }

  isSupported() {
    return 'Notification' in window;
  }

  hasPermission() {
    return this.permission === 'granted';
  }

  async requestPermission() {
    if (!this.isSupported()) {
      return false;
    }

    try {
      this.permission = await Notification.requestPermission();
      return this.permission === 'granted';
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  }

  showNotification(title, options = {}) {
    if (!this.enabled || !this.hasPermission()) {
      return;
    }

    // Don't show notification if user is viewing the same ticket and tab is visible
    if (options.data?.ticketId && 
        options.data.ticketId === this.currentTicketId && 
        document.visibilityState === 'visible') {
      return;
    }

    // Use a proper PNG icon for notifications (favicon.ico doesn't work well)
    // If contact has a profile pic, try to use it, otherwise use the app icon
    let notificationIcon = '/android-chrome-192x192.png';
    if (options.icon && options.icon.startsWith('http')) {
      // If it's a full URL (like contact profile pic), use it
      notificationIcon = options.icon;
    } else if (options.icon && options.icon.startsWith('/')) {
      // If it's a relative path, use it
      notificationIcon = options.icon;
    }

    const notificationOptions = {
      body: options.body || '',
      icon: notificationIcon,
      badge: '/android-chrome-192x192.png',
      tag: options.tag || 'message',
      data: options.data || {},
      requireInteraction: false,
      vibrate: [200, 100, 200],
      silent: false, // Ensure sound plays
      ...options
    };

    // Try to use service worker notification first (for PWA - works even when app is in background)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // Service worker notifications work even when app is closed/background (PWA)
        registration.showNotification(title, notificationOptions).catch((err) => {
          console.error('Error showing service worker notification:', err);
          // Fallback to regular notification
          this.showRegularNotification(title, notificationOptions);
        });
      }).catch(() => {
        // Fallback to regular notification
        this.showRegularNotification(title, notificationOptions);
      });
    } else {
      // Fallback to regular notification (desktop)
      this.showRegularNotification(title, notificationOptions);
    }
  }

  showRegularNotification(title, notificationOptions) {
    try {
      const notification = new Notification(title, notificationOptions);
      
      // Handle click to navigate to ticket
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        const ticketId = notificationOptions.data?.ticketId;
        const url = ticketId ? `/tickets/${ticketId}` : '/tickets';
        
        // Use window.location for navigation (works for both regular and PWA)
        window.location.href = url;
        notification.close();
      };
    } catch (err) {
      console.error('Error showing notification:', err);
    }
  }

  handleNewMessage(message, ticket) {
    if (!message || !ticket) return;

    // Don't notify for messages sent by the current user (fromMe: true)
    // unless it's a system message
    if (message.fromMe && message.nameUser !== 'SYSTEM' && message.nameUser !== 'SISTEMA') {
      return;
    }

    let contactName = getTicketDisplayName(ticket) || "Contacto";
    if (ticket.isGroup && !message.fromMe && message.nameUser) {
      contactName = `${contactName} · ${message.nameUser}`;
    }
    let messagePreview = '';

    // Format message preview based on type
    if (message.mediaType === 'image') {
      messagePreview = '📷 Imagen';
    } else if (message.mediaType === 'video') {
      messagePreview = '🎥 Vídeo';
    } else if (message.mediaType === 'audio') {
      messagePreview = '🎵 Audio';
    } else if (message.mediaType === 'location') {
      messagePreview = '📍 Ubicación';
    } else if (message.mediaType === 'vcard') {
      messagePreview = '👤 Tarjeta de contacto';
    } else if (message.mediaType === 'call_received' || message.mediaType === 'call_accepted' || message.mediaType === 'call_rejected') {
      messagePreview = '📞 Llamada';
    } else if (message.body) {
      // Truncate text message
      messagePreview = message.body.length > 50 
        ? message.body.substring(0, 50) + '...' 
        : message.body;
    } else {
      messagePreview = 'Nuevo mensaje';
    }

    // Use contact profile pic if available and accessible, otherwise use app icon
    let notificationIcon = '/android-chrome-192x192.png';
    const resolvedIcon = resolveContactProfilePicUrl(ticket.contact?.profilePicUrl);
    if (resolvedIcon) {
      notificationIcon = resolvedIcon;
    }

    this.showNotification(contactName, {
      body: messagePreview,
      icon: notificationIcon,
      tag: `ticket-${ticket.id}`,
      data: {
        ticketId: ticket.id,
        messageId: message.id
      }
    });
  }

  handleNewInternalMessage(message, chat) {
    if (!message || !chat) return;

    // Do not notify on own messages
    if (message.userId && window?.currentUser && message.userId === window.currentUser.id) {
      return;
    }

    const title = chat.isGroup && chat.name ? chat.name : "Internal chat";
    let preview = "";

    if (message.mediaType === "image") {
      preview = "📷 Image";
    } else if (message.mediaType === "video") {
      preview = "🎥 Video";
    } else if (message.mediaType === "audio") {
      preview = "🎵 Audio";
    } else if (message.body) {
      preview =
        message.body.length > 50
          ? message.body.substring(0, 50) + "..."
          : message.body;
    } else {
      preview = "New message";
    }

    this.showNotification(title, {
      body: preview,
      tag: `internal-chat-${chat.id}`,
      data: {
        internalChatId: chat.id
      }
    });
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;

